import React, { createContext, useContext, useEffect, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import ApiService from 'services/ApiService';
import { repeatEffect, settingsConfig } from 'utils/commonFunc';
import styles from 'pages/css/itgCtrl.module.css';
import { useTranslation } from 'react-i18next';

const TrafficLosContext = createContext(null);
const losColors = {
    'A': 'var(--iv-07)', // Green 
    'B': 'var(--iv-06)', // Light Green
    'C': 'var(--iv-05)', // Yellow
    'D': 'var(--iv-04)', // Orange
    'E': 'var(--iv-03)', // Deep Orange
    'F': 'var(--iv-02)', // Red
    '-': 'var(--white-50)'  // Grey
};

export function SwItsTrafficLosProvider({ children }) {
    const [currentData, setCurrentData] = useState();

    useEffect(() => {
        return repeatEffect(async () => {
            const response = await ApiService.get("dashboard/swIts/GetServiceQuality");
            if (response.status === 200) {
                setCurrentData(response.data);
            }
        }, settingsConfig().contentUpdateDelaySec);
    }, []);

    return (
        <TrafficLosContext.Provider value={currentData}>
            {children}
        </TrafficLosContext.Provider>
    );
}

export function SwItsTrafficLosInfo() {
    const currentData = useContext(TrafficLosContext);
    if (!currentData) return null;

    const cityData = currentData.groupRows.find(row => row.sigId === "00000");

    if (!cityData) return null;

    const totalMi = cityData.miTotal;
    const totalTd = cityData.tdTotal;
    const changePct = totalTd > 0 ? (((totalTd - totalMi) / totalMi) * 100).toFixed(2) : 0;
    const changeValueClass = parseFloat(changePct) >= 0 ? 'plus' : 'minus';

    return (
        <div className='traffic-info flx flx_col a_center j_center'>
            <div className='flx a_flx_end j_center'>
                <span className='boxTit' style={{ fontSize: 'var(--ft-80)', fontWeight: 'var(--ft-Bold)' }}>
                    {totalTd}
                </span>
                <span style={{ fontSize: 'var(--ft-32)', fontWeight: 'var(--ft-Light)', color: 'var(--white)', margin: '0 0 20px 22px' }}>
                    건
                </span>
            </div>
            <div className='flx a_center j_center'>
                <span className='cmpr' style={{ marginRight: '10px' }}>전일 대비</span>
                <span
                    className={`cmpr ${changeValueClass}`}
                    style={{ fontWeight: 'var(--ft-Bold)' }}
                >
                    {Math.abs(changePct)}
                </span>
                <span className='cmpr' style={{ marginLeft: '10px' }}>
                    %
                </span>
            </div>
        </div>
    );
}

export function LosChartLabel() {
    return (
        <div className='losChartLeft innerBg'>
            <div className='losChartLabel flx a_center j_center' style={{ height: '100%' }}>
                <div className='losChartTime'>
                    <div>최근 1시간</div>
                    <div>월 평균</div>
                </div>
            </div>
        </div>
    );
}

const losLevels = ["A", "B", "C", "D", "E", "F", "-"];

export function SwItsTrafficLosGrid() {
    const currentData = useContext(TrafficLosContext);

    if (!currentData || currentData.groupRows.length === 0) return null;

    const sumLosCounts = (key) => {
        return losLevels.map((level) =>
            currentData.groupRows[0][key].reduce((acc, item) => {
                if (item.losLvl === level) {
                    return acc + item.losCnt;
                }
                return acc;
            }, 0)
        );
    };

    const recentHour = sumLosCounts("mi");
    const fourWeekAvg = sumLosCounts("td");

    return (
        <>
            <div className='15min flx a_center j_spc_btw'>
                {recentHour.map((count, index) => (
                    <p key={index} className='flx a_flx_end j_center'>
                        <span style={{ fontSize: 'var(--ft-36)', color: 'var(--white)' }}>{count}</span>
                        <span style={{ fontSize: 'var(--ft-16)', marginLeft: '2px', marginBottom: '8px', fontWeight: 'var(--ft-Light)', color: 'var(--white)' }}>개</span>
                    </p>
                ))}
            </div>
            <div className='4wk flx a_center j_spc_btw'>
                {fourWeekAvg.map((count, index) => (
                    <p key={index} className='flx a_flx_end j_center'>
                        <span style={{ fontSize: 'var(--ft-36)', color: 'var(--white)' }}>{count}</span>
                        <span style={{ fontSize: 'var(--ft-16)', marginLeft: '2px', marginBottom: '8px', fontWeight: 'var(--ft-Light)', color: 'var(--white)' }}>개</span>
                    </p>
                ))}
            </div>
        </>
    );
}




function combineLosData(groupRows) {
    const result = {
        mi: {},
        td: {},
        miTotal: 0,
        tdTotal: 0
    };

    for (const row of groupRows) {
        result.miTotal += row.miTotal;
        result.tdTotal += row.tdTotal;

        row.mi.forEach(({ losLvl, losCnt }) => {
            result.mi[losLvl] = (result.mi[losLvl] || 0) + losCnt;
        });

        row.td.forEach(({ losLvl, losCnt }) => {
            result.td[losLvl] = (result.td[losLvl] || 0) + losCnt;
        });
    }

    return result;
}


export function SwItsTrafficLosChart() {
    const currentData = useContext(TrafficLosContext);
    if (!currentData || !currentData.groupRows || currentData.groupRows.length === 0) {
        return null;
    }

    const combined = combineLosData(currentData.groupRows);
    const losLevels = ['A', 'B', 'C', 'D', 'E', 'F', '-'];

    const miData = {
        district: '수원시',
        period: '최근 1시간',
        total: combined.miTotal
    };
    const tdData = {
        district: '수원시',
        period: '4주 평균',
        total: combined.tdTotal
    };

    losLevels.forEach(lvl => {
        miData[lvl] = combined.miTotal > 0
            ? parseFloat(((combined.mi[lvl] || 0) / combined.miTotal * 100).toFixed(1))
            : 0;
        tdData[lvl] = combined.tdTotal > 0
            ? parseFloat(((combined.td[lvl] || 0) / combined.tdTotal * 100).toFixed(1))
            : 0;
    });

    // const chartData = [miData, tdData];
    const chartData = [tdData, miData];
    return (
        <div className='traffic-chart' style={{ width: '100%', height: '100%' }}>
            <ResponsiveBar
                data={chartData}
                keys={losLevels}
                indexBy={d => d.period}
                layout="horizontal"
                colors={({ id }) => losColors[id]}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                padding={0.3}
                axisTop={null}
                axisRight={null}
                axisLeft={null}
                axisBottom={null}
                enableGridX={false}
                enableGridY={false}
                labelSkipWidth={10}
                labelSkipHeight={12}
                label={d => d.value > 10 ? `${d.value}%` : ''}
                animate={true}
                enableLabel={true}
                motionConfig="instant"
                transitionMode="immediate"
                maxValue={100}
                tooltip={({ id, value, color }) => (
                    <div
                        style={{
                            color: 'black',
                            fontSize: '40px',
                            backgroundColor: 'rgba(20, 245, 245, 0.6)',
                            padding: '5px',
                            borderRadius: '5px',
                        }}
                    >
                        {id}: {value}%
                    </div>
                )}
            />
        </div>
    );
}

export function SwItsTrafficLosTotalCnt() {
    const currentData = useContext(TrafficLosContext);
     const { t, i18n } = useTranslation(); 
    if (!currentData || currentData.groupRows.length === 0) return null;
    const totalMiCount = currentData.groupRows[0].miTotal || 0;

    return (
        <>
           <h2 className={`boxTit${i18n.language === 'en' ? '_en' : ''}`}  style={{color:'var(--iv-06)'}}>{t('IntersectionLOS')}
             </h2>
            {/* <span className='subTit' style={{ fontWeight: 'var(--ft-Medium)' }}>
                ({totalMiCount.toLocaleString()}개소)
            </span> */}
        </>
    );
}

export function LosTitleWithCount() {
  const currentData = useContext(TrafficLosContext);
  if (!currentData || currentData.groupRows.length === 0) return null;
  const totalMiCount = currentData.groupRows[0].miTotal;

  return (
    <p className="tit">
      LOS
      <span  style={{ fontWeight: 'var(--ft-Medium)', fontSize:'var(--ft-20)', marginLeft:'4px'}}>
        ({totalMiCount.toLocaleString()} 개소)
      </span>
    </p>
  );
}

const losLevelsTemp = ["A", "B", "C", "D", "E", "F"];
export function SwItsTrafficLosModuleIntgrControl() {

    const currentData = useContext(TrafficLosContext);
    if (!currentData || currentData.groupRows.length === 0) return null;

    const firstGroup = currentData.groupRows[0];

    // "-" 레벨을 미리 필터링
    const filteredMi = firstGroup.mi.filter(item => item.losLvl !== "-");

    // 레벨별 카운트 계산
    const levelCounts = losLevelsTemp.map((level) => {
        const found = filteredMi.find((item) => item.losLvl === level);
        return found ? found.losCnt : 0;
    });

 //   const totalCount = firstGroup.miTotal;
    const totalCount = filteredMi.reduce((acc, item) => acc + item.losCnt, 0);

    const levelColors = {
        A: 'var(--iv-06)',
        B: 'var(--iv-05)',
        C: 'var(--iv-04)',
        D: 'var(--iv-03)',
        E: 'var(--iv-02)',
        F: 'var(--white-50)',
    };

    return (
        <div className='L_s flx flx_col'>
            <div className={`${styles.subTitle}`}>교차로LOS</div>
            <div className={`${styles.losConts} flx flx_col j_spc_btw`}>
                <div className={`${styles.total} flx a_center j_center`}>
                    <span>총 대상 교차로</span>
                    <span className={`${styles.fgr_itg}`} style={{ margin: '0 6px 10px 6px' }}>{totalCount}</span>
                    <span>개소</span>
                </div>
                <div className={`${styles.losDt} flx flx_col`}>
                    <div className={`${styles.losDtInBox} flx a_center j_spc_btw`}>
                        {losLevelsTemp.slice(0, 3).map((level, idx) => (
                            <div key={level} className={`${styles.losBox} flx flx_col a_center j_center`}>
                                <p className={`${styles.losTit}`}>{level}등급</p>
                                <span className={`${styles.fgr_itg}`} style={{ color: levelColors[level] }}>
                                    {levelCounts[idx]}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className={`${styles.losDtInBox} flx a_center j_spc_btw`}>
                        {losLevelsTemp.slice(3).map((level, idx) => (
                            <div key={level} className={`${styles.losBox} flx flx_col a_center j_center`}>
                                <p className={`${styles.losTit}`}>{level}등급</p>
                                <span className={`${styles.fgr_itg}`} style={{ color: levelColors[level] }}>
                                    {levelCounts[idx + 3]}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
